import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Pencil, Trash2, X } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminConfirmDialog from '@/components/admin/AdminConfirmDialog';
import toast from 'react-hot-toast';

const AdminArtistsPage: React.FC = () => {
  const artists = useAdminStore((s) => s.artists);
  const deleteArtist = useAdminStore((s) => s.deleteArtist);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search) return artists;
    const q = search.toLowerCase();
    return artists.filter(
      (a) =>
        a.name.en.toLowerCase().includes(q) ||
        a.name.ka.toLowerCase().includes(q) ||
        a.bio.en.toLowerCase().includes(q),
    );
  }, [artists, search]);

  const handleDelete = (): void => {
    if (!deleteId) return;
    deleteArtist(deleteId);
    toast.success('Artist deleted');
    setDeleteId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Artists</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {artists.length} total
          </p>
        </div>
        <Link
          to="/admin/artists/new"
          className="flex items-center gap-2 px-5 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
        >
          <Plus size={14} /> Add Artist
        </Link>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs min-w-[180px]">
          <Search
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search artists..."
            className="w-full pl-10 pr-8 py-2.5 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-[12px] font-medium outline-none focus:border-black/15 dark:focus:border-white/15 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <AnimatePresence mode="popLayout">
          {filtered.map((artist) => (
            <motion.div
              key={artist._id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="group bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all"
            >
              <div className="flex gap-4 p-4">
                {/* Portrait */}
                <div className="relative w-24 h-24 flex-shrink-0">
                  <img
                    src={artist.portrait}
                    alt={artist.name.en}
                    className="w-24 h-24 aspect-square rounded-2xl object-cover"
                  />
                  {/* Active dot */}
                  <span
                    className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-black ${
                      artist.isActive ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-bold truncate">{artist.name.en}</p>
                      <p className="text-[11px] text-black/30 dark:text-white/30 mt-0.5 line-clamp-2 leading-relaxed">
                        {artist.bio.en.length > 100
                          ? artist.bio.en.slice(0, 100) + '...'
                          : artist.bio.en}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    {/* Artwork count badge */}
                    <span className="text-[8px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">
                      {artist.artworkCount} artwork{artist.artworkCount !== 1 ? 's' : ''}
                    </span>
                    <span
                      className={`text-[8px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
                        artist.isActive
                          ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                          : 'bg-red-500/10 text-red-600 dark:text-red-400'
                      }`}
                    >
                      {artist.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Hover actions */}
              <div className="flex items-center gap-1 px-4 pb-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <Link
                  to={`/admin/artists/${artist._id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] rounded-lg transition-all"
                >
                  <Pencil size={11} /> Edit
                </Link>
                <button
                  onClick={() => setDeleteId(artist._id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-black/40 dark:text-white/40 hover:text-red-500 hover:bg-red-500/5 rounded-lg transition-all"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-16">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            No artists found
          </p>
        </div>
      )}

      {/* Delete dialog */}
      <AdminConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Artist"
        message="This will permanently remove this artist. Associated artworks will not be deleted but will lose their artist reference. This action cannot be undone."
      />
    </div>
  );
};

export default AdminArtistsPage;
